import './styles.scss'
import {
  TicketStatusPill,
  TicketStatusPillWithWinnings,
} from '../ticket-status-pill/ticket-status-pill'
import { AddonResultDetails, ListViewMatchDetails } from '../list-view/list-view'
import { NumberSelectionSummary } from '../number-selection-summary/number-selection-summary'
import { Utils } from '@Util/utils'
import { TicketFormAddOnList } from '../ticket-form-add-list/ticket-form-addon-list'
import { Button } from '@Components/common/button'
import { NAV_VALUES } from '@AppState/ticket-info'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'

export type TicketFormSummaryProps = CompletedTicketFormSummaryProps | PendingTicketFormSummaryProps

type CommonTicketFormSummaryProps = {
  addOnDetails: AddonResultDetails[]
  drawNumber: number
  endingDrawNumber?: number
  drawDate: string | null
  numbersPerRow: number
  numberSelections: ListViewMatchDetails[]
  iconUrl: string | null
  isTicketHistorySummary?: boolean
  multiplier?: number
  betAmountInCents?: number
  onTicketActionClick?: (action: NAV_VALUES, newTicket?: boolean) => void
}

export type AddOnDetails = {
  name: string
  color: string | null
}

export type CompletedTicketFormSummaryProps = {
  type: 'COMPLETED'
  totalWinnings: number
  onClick: () => void
} & CommonTicketFormSummaryProps

export type PendingTicketFormSummaryProps = {
  type: 'PENDING'
} & CommonTicketFormSummaryProps

const CLASS_NAME = `ticket-form-summary`

export const TicketFormSummary = (props: TicketFormSummaryProps) => {
  const hostConfig = useSelector((state: RootState) => state.hostConfig.config)

  const isCompleted = props.type === 'COMPLETED'

  const renderPendingLabel = () => {
    if (props.type !== 'PENDING') {
      return null
    }
    return (
      <div className={`${CLASS_NAME}__status-label`}>
        <TicketStatusPill text={'Pending'} iconUrl={null} status={'pending'} />
      </div>
    )
  }

  const renderResultPill = () => {
    return (
      <TicketStatusPillWithWinnings
        totalWinnings={props.type === 'PENDING' ? null : props.totalWinnings}
        type={props.type}
        iconUrl={props.iconUrl}
      />
    )
  }

  const renderNumberSelection = () => {
    return (
      <NumberSelectionSummary
        numberSelections={props.numberSelections}
        numbersPerRow={props.numbersPerRow}
        addOnDetails={props.addOnDetails}
      />
    )
  }

  return (
    <div className={`${CLASS_NAME}`}>
      <div className={`${CLASS_NAME}__general-info`}>
        <div className={`${CLASS_NAME}__draw-details`}>
          <div className={`${CLASS_NAME}__draw-details-section`}>
            <div className={`${CLASS_NAME}__draw-details-value`}>
              {props.drawNumber}
              {props.endingDrawNumber ? ` - ${props.endingDrawNumber}` : ''}
            </div>
            <div className={`${CLASS_NAME}__draw-details-label`}>
              {hostConfig?.ticketForm.drawNumberLabel ?? 'Draw Number'}
            </div>
          </div>
          {props.drawDate ? (
            <div className={`${CLASS_NAME}__draw-details-section`}>
              <div className={`${CLASS_NAME}__draw-details-value`}>
                {Utils.toShortDate(props.drawDate)}
              </div>
              <div className={`${CLASS_NAME}__draw-details-label`}>
                {hostConfig?.ticketForm.drawDateLabel ?? 'Draw Date'}
              </div>
            </div>
          ) : null}
        </div>
        {renderPendingLabel()}
      </div>
      <div
        className={`${CLASS_NAME}__form-info`}
        data-state={
          (isCompleted ?? false) ? (props.isTicketHistorySummary ? 'history' : 'completed') : null
        }
        onClick={
          props.type === 'COMPLETED' && (props as CompletedTicketFormSummaryProps).onClick
            ? props.onClick
            : () => {}
        }>
        <div
          className={`${CLASS_NAME}__result-pill`}
          data-state={(isCompleted ?? false) ? 'completed' : null}>
          {renderResultPill()}
        </div>

        {props.betAmountInCents && (
          <div className={`${CLASS_NAME}__bet_amount`}>
            {Utils.convertCentsNumberToDollarString(props.betAmountInCents) + ' Bet'}
          </div>
        )}
        <div className={`${CLASS_NAME}__number-selection`}>{renderNumberSelection()}</div>
        {props.type === 'COMPLETED' && !props.isTicketHistorySummary ? (
          <img
            className={`${CLASS_NAME}__breakdown-icon`}
            src="/icons/ticket-status-icons/details.png"
            role={'button'}
            alt={'show details'}
          />
        ) : null}
        {props.addOnDetails.length > 0 ? <hr className={`${CLASS_NAME}__divider`} /> : null}
        <TicketFormAddOnList addOnDetails={props.addOnDetails} multiplierValue={props.multiplier} />
        {props.isTicketHistorySummary && props.onTicketActionClick ? (
          <>
            <hr className={`${CLASS_NAME}__divider`} />
            <div className={`${CLASS_NAME}__footer`}>
              <Button
                text="Ticket List"
                onClick={() => props.onTicketActionClick?.(NAV_VALUES.LIST_TICKET)}
              />
              <Button
                text="Watch Ticket"
                onClick={() => props.onTicketActionClick?.(NAV_VALUES.WATCH_TICKET)}
              />
              <Button
                text="Play Again"
                onClick={() => props.onTicketActionClick?.(NAV_VALUES.EDIT, true)}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}
